import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** The mail as it will arrive, editable in place: the API's own rendered document, so the app cannot drift from it. */
const MailPreview = ({
  html,
  message,
  draftMessage,
  names,
  onChange,
  onPress,
}: {
  html: string;
  /** What the sender has already written, when they have been here before. */
  message: string;
  /** The words this document was rendered from, to tell an edit apart from the draft itself. */
  draftMessage: string;
  /** The addressees' first names, in the order they are written to. One name means one mail. */
  names: string[];
  /** The slot itself rides along, for a caller that reads more than its text. */
  onChange: (next: string, slot: HTMLElement) => void;
  /** A click landing in the mail, which the page around the frame cannot see for itself. */
  onPress?: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const frame = useRef<HTMLIFrameElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();
  // Read inside the frame's own listeners, which outlive the render that wired them.
  const namesRef = useRef(names);
  namesRef.current = names;
  const pressed = useRef(onPress);
  pressed.current = onPress;
  /** The one-off walk through the names when the step opens, which a hover interrupts. */
  const touring = useRef<ReturnType<typeof setTimeout>>();
  /** The wait before the opening play, and the beat between its two halves. */
  const opening = useRef<ReturnType<typeof setTimeout>>();
  useEffect(
    () => () => {
      clearTimeout(touring.current);
      clearTimeout(opening.current);
    },
    [],
  );
  /** The greeting's name: how to mark it, roll it to a given name, and put it back at rest. */
  const token = useRef<{
    node: HTMLElement;
    rest: () => void;
    mark: () => void;
    /** One name becoming a stand-in where it stands, then the wheel to the newest pick. */
    become: () => void;
    /** The greeting arriving: empty, then each name in turn. */
    arrive: () => void;
    unmark: () => void;
    rollTo: (name: string) => void;
  } | null>(null);
  /** What the names were last time, to tell a new addressee from a new selection of the same ones. */
  const shown = useRef<string[]>([]);

  // Both the stand-in and which name it shows change while the document stays loaded, so each move is animated from where it is.
  useEffect(() => {
    const { current } = token;
    const before = shown.current;
    shown.current = names;
    if (!current) return;

    if (names.length < 2) {
      current.unmark();
      current.rest();

      return;
    }

    current.node.setAttribute('aria-label', t('mailPreview.greetingNames', { names: names.join(', ') }));

    if (before.length === 0) {
      current.arrive();

      return;
    }

    if (before.length === 1) {
      current.become();

      return;
    }

    current.mark();
    if (before[0] !== names[0]) current.rollTo(names[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [names.join('|')]);
  // All the room the step gives, unless the edge would land inside a line; measured on line boxes, not elements.
  const snap = (): void => {
    const doc = frame.current?.contentDocument;
    const room = box.current?.clientHeight;
    if (!doc?.body || !room) return;

    const scrolled = doc.documentElement.scrollTop;
    const lines: { top: number; bottom: number }[] = [];
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, {
      acceptNode: node => (node.textContent?.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT),
    });
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      const range = doc.createRange();
      range.selectNodeContents(node);
      Array.from(range.getClientRects()).forEach(line =>
        // eslint-disable-next-line no-loop-func
        lines.push({ top: line.top + scrolled, bottom: line.bottom + scrolled }),
      );
    }
    // The logo carries no text and is cut just as visibly as a word is.
    doc.body.querySelectorAll('img').forEach(mark => {
      const drawn = mark.getBoundingClientRect();
      lines.push({ top: drawn.top + scrolled, bottom: drawn.bottom + scrolled });
    });

    const across = lines.filter(line => line.top < room - 0.5 && line.bottom > room + 0.5);
    // Never pulled back more than a line: past that it is a block clipped, not a word sliced.
    const stops = across.map(line => line.top).filter(top => top > room - 56);
    setHeight(stops.length ? Math.floor(Math.min(...stops)) : room);
  };

  useEffect(() => {
    const node = box.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => snap());
    observer.observe(node);

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLoad = (): void => {
    const doc = frame.current?.contentDocument;
    const node = doc?.querySelector<HTMLElement>('[data-mail-slot="message"]');
    if (!doc || !node) return;

    // Enough of an affordance to say "this part is yours", without dressing the mail up as a form.
    const style = doc.createElement('style');
    style.textContent = `
      [data-mail-slot='message'] { outline: 1px dashed #d3d8de; outline-offset: 8px; border-radius: 2px; }
      [data-mail-slot='message']:hover { outline-color: #9aa3ad; }
      [data-mail-slot='message']:focus { outline: 1px dashed #9aa3ad; outline-offset: 8px; }
      /* The mail carries no font of its own, on purpose: a mail client paints it in whatever it sets
         its own text in, which is a sans-serif everywhere anybody reads mail. Raw HTML in an iframe has
         no client to do that and falls back to Times, so the preview would show a serif mail nobody
         will ever receive. Named here rather than in the mail, where it would override the client. */
      body { font-family: Arial, Helvetica, sans-serif; }
      /* A mail client's text size, not the frame's 16px default, which drew everything a size too large. */
      body { font-size: 14px; }
      /* The preview's own gutter, not the mail's: it reads the same whether the mail carries padding or none. */
      body > table > tbody > tr > td > table { padding: 0 !important; }
      body > table > tbody > tr > td { padding: 12px 26px !important; }
      /* The paragraph the draft leaves blank is the one the sender is here to write, so it says so
         until they do. In the preview only: the mail carries an empty line, not this text.

         It holds its line whether or not the prompt is in it: a paragraph whose only content is a
         space has no line box at all, so losing the prompt collapsed the gap the summary goes in. */
      p[data-mail-summary] { min-height: var(--mail-line, 24px); }
      p[data-mail-blank]::before { content: attr(data-mail-blank); color: #9aa3ad; }
      /* Anchored even while no name stands for several, or the hidden list lands below the mail and makes it scroll. */
      [data-mail-slot='greeting-name'] { position: relative; }
      /* A name that stands for several: bold, in the app's own orange, and nothing drawn around it —
         a box made the greeting read as a form field. The mail itself carries a plain name; this is
         only the preview saying the name is per recipient. */
      [data-mail-token] { position: relative; display: inline-block; font-weight: 700; color: #ee4a19; cursor: default; }
      /* The name that moves is the span inside, never the hovered element: sliding what the pointer is
         on fires mouseleave mid-roll. Nothing is clipped — an inline-block that clips takes its
         baseline from its bottom edge, and no amount of correcting that kept the name on its line. */
      [data-mail-token] > span { display: inline-block; }
      /* The fade runs a little ahead of the travel: enough that a name is not legible a line away from
         where it belongs, not so much that it blinks out the moment it moves. */
      [data-mail-token] > span[data-mail-roll='out'] { transform: translateY(-70%); opacity: 0; transition: transform 190ms ease-in, opacity 150ms ease-in; }
      [data-mail-token] > span[data-mail-roll='under'] { transform: translateY(70%); opacity: 0; transition: none; }
      [data-mail-token] > span[data-mail-roll='in'] { transform: translateY(0); opacity: 1; transition: transform 220ms ease-out, opacity 175ms ease-out 45ms; }
      /* Who the stand-in stands for, all at once, under the greeting. Not interactive: a pointer that
         can enter it can also leave it, and the label would flicker as it went.

         It opens from the name's own corner, and the rows rise into it one after another — the same
         movement the greeting makes, so the list reads as the wheel laid out flat rather than as a
         tooltip that happens to be nearby. */
      [data-mail-names] { position: absolute; top: calc(100% + 9px); left: -12px; z-index: 2; pointer-events: none; padding: 7px 13px 8px; border: 1px solid #e7eaee; border-radius: 9px; background: #fff; box-shadow: 0 1px 2px rgba(21, 27, 36, 0.05), 0 12px 28px -6px rgba(21, 27, 36, 0.16); white-space: nowrap; font-size: 13px; font-weight: 400; line-height: 21px; color: #151b24; opacity: 0; transform: translateY(-5px) scale(0.97); transform-origin: 12px -9px; transition: opacity 130ms ease, transform 190ms cubic-bezier(0.2, 0.8, 0.3, 1); }
      [data-mail-names][data-mail-shown] { opacity: 1; transform: none; }
      [data-mail-names] > p { margin: 0 0 6px; padding-bottom: 6px; border-bottom: 1px solid #f1f3f6; font-size: 11px; line-height: 15px; color: #9aa3ad; }
      /* Every row wears the merge field's own orange and weight: each one is a name the greeting can
         be, and a plain list under a bold orange name read as two different things. */
      [data-mail-names] > div { font-weight: 700; color: #ee4a19; opacity: 0; transform: translateY(6px); transition: opacity 160ms ease, transform 190ms cubic-bezier(0.2, 0.8, 0.3, 1); }
      [data-mail-names][data-mail-shown] > div { opacity: 1; transform: none; }
      /* On the way out they leave together: a stagger played backwards reads as the list unravelling. */
      [data-mail-names]:not([data-mail-shown]) > div { transition-delay: 0ms !important; }
      @media (prefers-reduced-motion: reduce) { [data-mail-token] > span, [data-mail-names], [data-mail-names] > div { transition: none !important; } }
    `;
    doc.head.appendChild(style);

    // That one paragraph, not whichever is empty: Enter makes another, and every blank line would offer the prompt.
    const summaryParagraph = [...node.querySelectorAll('p')].find(paragraph => !paragraph.textContent?.trim());
    // The prompt is spent when the caret leaves and never comes back: it only says where to start.
    if (summaryParagraph) {
      summaryParagraph.setAttribute('data-mail-summary', '');
      const paragraphLine = doc.defaultView?.getComputedStyle(summaryParagraph).lineHeight;
      // `normal` where the mail sets no line-height: not a length, so min-height would be dropped entirely.
      if (paragraphLine?.endsWith('px')) summaryParagraph.style.setProperty('--mail-line', paragraphLine);
    }

    let promptSpent = false;
    const markBlank = (): void => {
      if (!summaryParagraph?.isConnected) return;
      if (promptSpent || summaryParagraph.textContent?.trim()) summaryParagraph.removeAttribute('data-mail-blank');
      else summaryParagraph.setAttribute('data-mail-blank', t('mailPreview.summaryPlaceholder'));
    };
    const spendPrompt = (): void => {
      if (promptSpent) return;
      promptSpent = true;
      markBlank();
    };

    /** The caret at the start of the summary, which is where this step opens and what it is for. */
    const caretToSummary = (): void => {
      if (!summaryParagraph?.isConnected) return;
      const range = doc.createRange();
      range.setStart(summaryParagraph, 0);
      range.collapse(true);
      const selection = doc.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      node.focus();
    };

    // Bounded by the slot: the mail's own layout is a table too, which every caret is inside.
    const cellOf = (at: Node | null): Element | null => {
      const cell = (at?.nodeType === Node.ELEMENT_NODE ? (at as Element) : at?.parentElement)?.closest('td');
      return cell && node.contains(cell) ? cell : null;
    };

    /** A block's paragraph on the given side: a summary of several paragraphs is one block. */
    const paragraphOf = (block?: ChildNode | null, last = false): HTMLElement | null => {
      if (block?.nodeType !== Node.ELEMENT_NODE) return null;
      const element = block as HTMLElement;
      if (element.tagName === 'P') return element;
      const paragraphs = element.querySelectorAll<HTMLElement>('p');
      return paragraphs[last ? paragraphs.length - 1 : 0] ?? null;
    };

    // The table is only as wide as its figures, so a click beside a row lands below the table unless it is placed in the row.
    node.addEventListener('mousedown', event => {
      if (cellOf(event.target as Node | null)) return;
      const row = [...node.querySelectorAll('tr')].find(each => {
        const { top, bottom, right } = each.getBoundingClientRect();
        return event.clientX > right && event.clientY >= top && event.clientY < bottom;
      });
      const cell = row?.cells[row.cells.length - 1];
      if (!cell) return;
      event.preventDefault();
      node.focus();
      const range = doc.createRange();
      range.selectNodeContents(cell);
      range.collapse(false);
      const selection = doc.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    });

    // The paragraph holds a space so it survives being split; the caret would otherwise land after it.
    node.addEventListener('mousedown', event => {
      const paragraph = (event.target as HTMLElement | null)?.closest('p');
      if (!paragraph || paragraph.textContent?.trim()) return;
      event.preventDefault();
      const range = doc.createRange();
      range.setStart(paragraph, 0);
      range.collapse(true);
      const selection = doc.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      node.focus();
    });

    /** The caret on the blank line under the table, opening one when it has been typed on. */
    const openLineBelow = (table: Element): void => {
      const below = paragraphOf(table.nextSibling);
      const selection = doc.getSelection();
      if (!below || !selection) return;
      selection.collapse(below, 0);
      if (below.firstChild?.nodeName !== 'BR') doc.execCommand('insertLineBreak');
      selection.collapse(below, 0);
    };

    // The mail's two-line gaps around the breakdown are real empty lines here, so a click in one has a line to land on; the read-back trims them.
    const breaks = (count: number): HTMLElement[] => Array.from({ length: count }, () => doc.createElement('br'));
    node.querySelectorAll('table').forEach(table => {
      const below = paragraphOf(table.nextSibling);
      if (below) {
        table.style.setProperty('margin-bottom', '0');
        below.prepend(...breaks(2));
      }
      const heading = table.previousElementSibling?.hasAttribute('data-mail-heading')
        ? (table.previousElementSibling as HTMLElement)
        : table;
      const above = paragraphOf(heading.previousSibling, true);
      if (above) {
        heading.style.marginTop = '0';
        above.style.marginBottom = '0';
        // One more than the lines it makes: a break that ends a paragraph draws none.
        above.append(...breaks(3));
      }
    });

    // A caret between two blocks types a bare line into neither, so it is moved into the paragraph next to it.
    const settleCaret = (): void => {
      const selection = doc.getSelection();
      if (!selection?.isCollapsed || selection.anchorNode !== node) return;
      const after = paragraphOf(node.childNodes[selection.anchorOffset]);
      const target = after ?? paragraphOf(node.childNodes[selection.anchorOffset - 1], true);
      if (!target) return;
      const range = doc.createRange();
      range.selectNodeContents(target);
      range.collapse(!!after);
      selection.removeAllRanges();
      selection.addRange(range);
    };
    // On keydown too: selectionchange is queued, and a key pressed straight after an arrow lands first.
    doc.addEventListener('selectionchange', settleCaret);
    node.addEventListener('keydown', settleCaret);

    // Enter is a line break, not a paragraph: the mail's 20px gap would otherwise follow one press.
    node.addEventListener('keydown', event => {
      if (event.key !== 'Enter') return;
      const selection = doc.getSelection();
      const cell = cellOf(selection?.anchorNode ?? null);
      // Shift+Enter is the browser's own line break, which only a cell has to refuse.
      if (event.shiftKey && !cell) return;
      event.preventDefault();
      if (!cell) {
        doc.execCommand('insertLineBreak');
        return;
      }
      // A break inside a cell is read back as a row the API never drew; the last row opens a line under the table instead.
      const table = cell.closest('table');
      if (table && cell.closest('tr') === table.rows[table.rows.length - 1]) openLineBelow(table);
    });

    // Backspace at the start of a cell walks back into the cell before, as in a document; a row left empty goes.
    node.addEventListener('keydown', event => {
      const selection = doc.getSelection();
      const cell = cellOf(selection?.anchorNode ?? null);
      if (event.key !== 'Backspace' || !selection?.isCollapsed || !selection.anchorNode || !cell) return;
      const typedBefore = doc.createRange();
      typedBefore.setStart(cell, 0);
      typedBefore.setEnd(selection.anchorNode, selection.anchorOffset);
      if (typedBefore.toString()) return;
      event.preventDefault();
      const row = cell.closest('tr');
      const previous = cell.previousElementSibling ?? row?.previousElementSibling?.lastElementChild;
      if (!row || !previous) return;
      if (!cell.previousElementSibling && !row.textContent?.trim()) {
        row.remove();
        node.dispatchEvent(new Event('input'));
      }
      const range = doc.createRange();
      range.selectNodeContents(previous);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    });

    // What Backspace or Delete next to the table does instead of merging into it: the blank line nearest it goes first.
    const trimBesideTable = (block: ChildNode | undefined, fromStart: boolean): void => {
      const paragraph = paragraphOf(block, !fromStart);
      const selection = doc.getSelection();
      if (!block || !paragraph || !selection) return;
      let edge = fromStart ? paragraph.firstChild : paragraph.lastChild;
      while (edge?.nodeType === Node.TEXT_NODE && !edge.textContent?.trim())
        edge = fromStart ? edge.nextSibling : edge.previousSibling;
      const range = doc.createRange();
      if (edge?.nodeName === 'BR') range.selectNode(edge);
      else if (!paragraph.textContent?.trim()) range.selectNode(block);
      else {
        // No blank line left: the caret steps into the table, so the next press deletes from its nearest figure.
        const table = (fromStart ? block.previousSibling : block.nextSibling) as HTMLTableElement | null;
        const rows = table?.rows;
        const cell = fromStart ? rows?.[rows.length - 1]?.lastElementChild : rows?.[0]?.firstElementChild;
        if (!cell) return;
        range.selectNodeContents(cell);
        range.collapse(!fromStart);
        selection.removeAllRanges();
        selection.addRange(range);
        return;
      }
      selection.removeAllRanges();
      selection.addRange(range);
      // execCommand rather than remove(), so the edit is undoable and reaches the input listener.
      doc.execCommand('delete');
    };

    // An edit stays inside one cell or off the table: Chrome's Backspace after it moves the caret into its last cell.
    node.addEventListener('beforeinput', event => {
      const tables = [...node.querySelectorAll('table')];
      const isTable = (child?: Node): boolean => child?.nodeName === 'TABLE';
      const deleting = event.inputType.startsWith('delete');
      let beside: { block: ChildNode; fromStart: boolean } | undefined;
      const crosses = event.getTargetRanges().some(target => {
        const cell = cellOf(target.startContainer);
        if (cell && cell === cellOf(target.endContainer)) return false;
        // Chrome reports a merge into the table as a range that starts just past it, not inside it.
        const { startContainer, startOffset, endContainer, endOffset } = target;
        if (deleting && isTable(startContainer.childNodes[startOffset - 1])) {
          beside = { block: startContainer.childNodes[startOffset], fromStart: true };
          return true;
        }
        if (deleting && isTable(endContainer.childNodes[endOffset])) {
          beside = { block: endContainer.childNodes[endOffset - 1], fromStart: false };
          return true;
        }
        const range = doc.createRange();
        range.setStart(startContainer, startOffset);
        range.setEnd(endContainer, endOffset);
        // A selection over the whole table may still delete it, as a document would.
        return tables.some(table => {
          const whole = doc.createRange();
          whole.selectNode(table);
          const covers =
            range.compareBoundaryPoints(Range.START_TO_START, whole) <= 0 &&
            range.compareBoundaryPoints(Range.END_TO_END, whole) >= 0;
          return range.intersectsNode(table) && !covers;
        });
      });
      if (!crosses) return;
      event.preventDefault();
      if (beside) trimBesideTable(beside.block, beside.fromStart);
    });

    // One name standing in for several, bolded and rolled through the real ones; the names are on the title too.
    const greeted = doc.querySelector<HTMLElement>('[data-mail-slot="greeting-name"]');
    if (greeted) {
      const rendered = greeted.textContent ?? '';
      // The space belongs to the name: with nobody to write to the greeting is "Dear,".
      const lead = greeted.previousSibling;
      const leading = lead?.nodeType === 3 ? (lead.textContent ?? '') : '';
      // No mask: an inline-block with hidden overflow baselines on its bottom edge and drags the name off the line.
      const moving = doc.createElement('span');
      const name = doc.createElement('span');
      name.textContent = rendered;
      greeted.textContent = '';
      moving.appendChild(name);
      greeted.appendChild(moving);

      // The comma rides inside the moving span, or every name of a different length nudges it sideways.
      const trailing = greeted.nextSibling;
      if (trailing?.nodeType === 3 && trailing.textContent) {
        const tail = doc.createElement('span');
        tail.textContent = trailing.textContent;
        tail.style.fontWeight = '400';
        tail.style.color = doc.defaultView?.getComputedStyle(greeted.parentElement as Element).color ?? 'inherit';
        trailing.remove();
        moving.appendChild(tail);
      }

      // Hover lists the names instead of speeding the wheel: by then the reader wants who it stands for.
      const listed = doc.createElement('div');
      listed.setAttribute('data-mail-names', '');
      const said = doc.createElement('p');
      said.textContent = t('mailPreview.greetingEach');
      listed.appendChild(said);
      greeted.appendChild(listed);

      let landing: ReturnType<typeof setTimeout>;
      let arriving: ReturnType<typeof setTimeout>;
      const still = !!doc.defaultView?.matchMedia('(prefers-reduced-motion: reduce)').matches;

      /** The name and the space in front of it, which belongs to the name rather than to the "Dear". */
      const write = (next: string): void => {
        name.textContent = next;
        if (lead && leading) lead.textContent = next ? leading : leading.replace(/[ \u00a0]+$/, '');
      };

      /** One turn of the wheel, landing on a given name: out the top, under, and up into place. */
      const rollTo = (next: string): void => {
        if (still) {
          write(next);

          return;
        }
        moving.setAttribute('data-mail-roll', 'out');
        clearTimeout(landing);
        landing = setTimeout(() => {
          write(next);
          moving.setAttribute('data-mail-roll', 'under');
          clearTimeout(arriving);
          // A frame at the bottom before it starts up, or the browser skips the travel entirely.
          arriving = setTimeout(() => moving.setAttribute('data-mail-roll', 'in'), 30);
        }, 190);
      };

      const rest = (): void => {
        clearTimeout(touring.current);
        clearTimeout(opening.current);
        clearTimeout(landing);
        clearTimeout(arriving);
        moving.removeAttribute('data-mail-roll');
        // Cleared recipients means "Dear,", not the name it was drafted for, which is what the mail says.
        write(namesRef.current[0] ?? '');
      };

      /** A name standing for several. Walked from plain to bold width, since bold is not animatable in this font. */
      const mark = (smoothly = false): void => {
        const already = greeted.hasAttribute('data-mail-token');
        const label = t('mailPreview.greetingNames', { names: namesRef.current.join(', ') });
        if (already || !smoothly || still) {
          greeted.setAttribute('data-mail-token', '');
          greeted.setAttribute('aria-label', label);

          return;
        }

        // Both read before it is marked, or there is nothing for the colour and the width to travel from.
        const plain = greeted.getBoundingClientRect().width;
        greeted.style.color = doc.defaultView?.getComputedStyle(greeted).color ?? '';
        greeted.setAttribute('data-mail-token', '');
        greeted.setAttribute('aria-label', label);
        const bold = greeted.getBoundingClientRect().width;
        greeted.style.width = `${plain}px`;
        // Read once so the browser has the old width to move from, rather than jumping to the new one.
        greeted.getBoundingClientRect();
        greeted.style.transition = 'width 220ms ease, color 220ms ease';
        greeted.style.width = `${bold}px`;
        greeted.style.color = '';
        setTimeout(() => {
          greeted.style.transition = '';
          greeted.style.width = '';
        }, 260);
      };

      /** The name already standing is marked in place first, and only then does the wheel turn. */
      const become = (): void => {
        mark(true);
        clearTimeout(opening.current);
        opening.current = setTimeout(() => rollTo(namesRef.current[0]), 280);
      };

      const unmark = (): void => {
        greeted.removeAttribute('data-mail-token');
        greeted.removeAttribute('aria-label');
        greeted.style.minWidth = '';
      };

      /** The arrival, replayed whenever the greeting becomes a stand-in again. Nothing to play if still. */
      let arrive = (): void => {
        rest();
        if (namesRef.current.length > 1) mark();
      };

      if (!still) {
        // One turn on opening: a stand-in that never moves is a stand-in nobody knows about.
        const walk = (step: number): void => {
          const all = namesRef.current;
          if (step >= all.length) {
            rollTo(all[0]);

            return;
          }
          rollTo(all[step]);
          touring.current = setTimeout(() => walk(step + 1), 1000);
        };

        // Every name is seen arriving, the first included: a name already sitting there reads as always having been.
        arrive = (): void => {
          const all = namesRef.current;
          rest();
          if (all.length < 2) return;
          mark();
          write(all[1]);
          moving.setAttribute('data-mail-roll', 'under');
          opening.current = setTimeout(() => {
            moving.setAttribute('data-mail-roll', 'in');
            touring.current = setTimeout(() => walk(2), 1000);
          }, 450);
        };
      }

      token.current = { node: greeted, rest, mark, unmark, rollTo, become, arrive: () => arrive() };
      arrive();

      greeted.addEventListener('mouseenter', () => {
        const all = namesRef.current;
        if (all.length < 2) return;
        listed.querySelectorAll('div').forEach(row => row.remove());
        all.forEach((each, at) => {
          const row = doc.createElement('div');
          row.textContent = each;
          // Each name comes up after the one above it, the way they come up in the greeting itself.
          row.style.transitionDelay = `${70 + at * 45}ms`;
          listed.appendChild(row);
        });
        // A frame at the starting state, or the rows are already in place when the transition begins.
        doc.defaultView?.requestAnimationFrame(() => listed.setAttribute('data-mail-shown', ''));
      });
      greeted.addEventListener('mouseleave', () => listed.removeAttribute('data-mail-shown'));
    }

    // Pointer events stay in the frame's own document, so the click is passed out as a call instead.
    doc.addEventListener('pointerdown', () => pressed.current?.());

    // The mail's links carry no target, so in a frame they would load the site where the mail was.
    doc.addEventListener('click', event => {
      const link = (event.target as Element | null)?.closest('a');
      const href = link?.getAttribute('href');
      event.preventDefault();
      // A preview's stand-in link points nowhere: each recipient's copy carries its own.
      if (!href || href.startsWith('#')) return;
      // A mailto handed to a new tab leaves an empty one behind, so it goes to the page around us.
      if (href.startsWith('mailto:')) window.location.href = href;
      else window.open(href, '_blank', 'noopener,noreferrer');
    });

    node.contentEditable = 'true';
    node.spellcheck = true;
    node.setAttribute('role', 'textbox');
    node.setAttribute('aria-label', t('mailPreview.message'));
    // Only a real edit is written back: comparing against the node's text flattened the paragraphs.
    if (message && message !== draftMessage) node.innerText = message;
    markBlank();

    // Caret set before focusing, or the browser puts it at the top and spends the prompt unseen.
    caretToSummary();
    doc.addEventListener('selectionchange', () => {
      const anchor = doc.getSelection()?.anchorNode;
      if (!anchor || !summaryParagraph?.contains(anchor === summaryParagraph ? anchor : anchor.parentNode)) {
        spendPrompt();
      }
    });
    node.addEventListener('blur', spendPrompt);
    node.addEventListener('input', () => {
      // Trailing blanks belong to the draft's own empty paragraph, not to what the sender typed.
      onChange(node.innerText.replace(/[ \t]+$/gm, ''), node);
      markBlank();
      snap();
    });

    // Columns held at their drawn width, so shortening the longest label does not slide every figure sideways.
    const holdColumns = (): void =>
      node.querySelectorAll('td').forEach(cell => {
        const { paddingLeft, paddingRight } = doc.defaultView?.getComputedStyle(cell) ?? {};
        const content =
          cell.getBoundingClientRect().width - parseFloat(paddingLeft ?? '0') - parseFloat(paddingRight ?? '0');
        cell.style.setProperty('width', `${content}px`);
      });

    // Last of all, and again once the fonts are in: everything above moves the mail's lines.
    requestAnimationFrame(() => {
      holdColumns();
      snap();
    });
    doc.fonts?.ready.then(() => {
      holdColumns();
      snap();
    });
  };

  return (
    // Ends on the last line that fits whole; what is left is the card's white, which is the mail's too.
    <div ref={box} className="min-h-0 w-full flex-1 bg-white">
      <iframe
        ref={frame}
        title={t('mailPreview.title')}
        srcDoc={html}
        onLoad={onLoad}
        style={{ height: height ?? '100%' }}
        className="w-full border-0 bg-white"
      />
    </div>
  );
};

export default MailPreview;
